import { apiClient } from "./client";

/**
 * Get a presigned URL from the backend that the browser can PUT a file to.
 *
 * Returns:
 *   { uploadUrl, s3Key, expiresInSeconds }
 *
 * The s3Key is what we save to the database after a successful upload.
 */
export async function createUploadUrl({
  category,
  fileName,
  contentType,
  fileSizeBytes,
}) {
  const res = await apiClient.post("/storage/upload-url", {
    category,
    fileName,
    contentType,
    fileSizeBytes,
  });
  return res.data;
}

/**
 * Get a presigned URL for downloading/viewing a file.
 */
export async function createDownloadUrl(s3Key) {
  const res = await apiClient.get("/storage/download-url", {
    params: { key: s3Key },
  });
  return res.data;
}

/**
 * High-level upload helper. Given a File object, uploads it to S3 via
 * presigned URL and returns the s3Key plus original file name.
 *
 * Throws if the file fails any client-side check (size, type) or if S3
 * rejects the upload.
 *
 * Usage in a form component:
 *   const file = e.target.files[0];
 *   try {
 *     const { s3Key, fileName } = await uploadFile(file, "certificate");
 *     setCertificateInfo({ s3Key, fileName });
 *   } catch (err) {
 *     showError(err.message);
 *   }
 */
export async function uploadFile(file, category = "certificate") {
  // Client-side checks before bothering the server
  const MAX_BYTES = 5 * 1024 * 1024;
  const ALLOWED_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/svg+xml",
  ];
  const ALLOWED_LOGO_TYPES = ["image/jpeg", "image/png"];
  const ALLOWED_LOGO_EXTS = [".png", ".jpg", ".jpeg"];

  if (!file) throw new Error("No file selected.");
  if (file.size > MAX_BYTES) {
    throw new Error("File is too large. Maximum size is 5 MB.");
  }

  if (category === "logo") {
    const ext = file.name
      ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
      : "";
    if (
      !ALLOWED_LOGO_TYPES.includes(file.type) ||
      !ALLOWED_LOGO_EXTS.includes(ext)
    ) {
      throw new Error("Only PNG and JPEG images are accepted for logos.");
    }
  } else if (category === "resume") {
    const ext = file.name
      ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
      : "";
    const ALLOWED_RESUME_EXTS = [".pdf", ".docx", ".doc", ".jpg", ".jpeg", ".png"];
    const ALLOWED_RESUME_TYPES = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
      "image/jpeg",
      "image/png",
    ];

    let resolvedType = file.type;
    if (!resolvedType || resolvedType === "application/octet-stream") {
      if (ext === ".pdf") resolvedType = "application/pdf";
      else if (ext === ".docx")
        resolvedType =
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      else if (ext === ".doc") resolvedType = "application/msword";
      else if (ext === ".png") resolvedType = "image/png";
      else if (ext === ".jpg" || ext === ".jpeg") resolvedType = "image/jpeg";
    }

    if (!ALLOWED_RESUME_EXTS.includes(ext) && !ALLOWED_RESUME_TYPES.includes(resolvedType)) {
      throw new Error("Only PDF, Word (.doc, .docx), and Image (JPEG, PNG) files are accepted for resumes.");
    }

    // Step 1: ask the backend for a presigned URL
    const { uploadUrl, s3Key } = await createUploadUrl({
      category,
      fileName: file.name,
      contentType: resolvedType || file.type || "application/pdf",
      fileSizeBytes: file.size,
    });

    // Step 2: PUT the file directly
    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": resolvedType || file.type || "application/pdf" },
      body: file,
    });

    if (!putRes.ok) {
      const detail = await putRes.text().catch(() => "");
      throw new Error(
        `Upload failed (${putRes.status}). ${detail.slice(0, 200)}`,
      );
    }

    return { s3Key, fileName: file.name, contentType: resolvedType };
  } else if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error("Only PDF, JPEG, PNG, WebP, and SVG files are accepted.");
  }

  // Step 1: ask the backend for a presigned URL
  const { uploadUrl, s3Key } = await createUploadUrl({
    category,
    fileName: file.name,
    contentType: file.type,
    fileSizeBytes: file.size,
  });

  // Step 2: PUT the file directly to S3. Note: don't use apiClient here -
  // the URL is already signed, and we don't want our auth interceptor adding
  // a Bearer token header (S3 will reject it as an unrecognised header).
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!putRes.ok) {
    const detail = await putRes.text().catch(() => "");
    throw new Error(
      `Upload failed (${putRes.status}). ${detail.slice(0, 200)}`,
    );
  }

  return { s3Key, fileName: file.name };
}
